// optionUtils.ts
import type * as echarts from 'echarts';
// dprFonts.ts
export const scaleFontByDpr = (base: number, dpr: number) => {
  // 1.00 → 1.00x, 1.25 → 0.92x, 1.50 → 0.85x, 2.00 → 0.75x (tweak to taste)
  const factor = 1 - (Math.max(dpr, 1) - 1) * 0.3;
  const scaled = base * factor;
  // clamp so things never get comically tiny/huge
  return Math.round(Math.min(base, Math.max(base * 0.7, scaled)));
};

export const scaleLinearByDpr = (base: number, dpr: number) => {
  // Same curve but without rounding/clamp as aggressively
  const factor = 1 - (Math.max(dpr, 1) - 1) * 0.3;
  const scaled = base * factor;
  return Math.max(base * 0.6, Math.min(base, +scaled.toFixed(2)));
};

export function prepareServerOption(rawJson: string, dpr: number): echarts.EChartsOption {
  const opt = JSON.parse(rawJson);

  if (!opt) return {};

  // --- existing fixes ---
  if (Array.isArray(opt.dataZoom)) {
    opt.dataZoom = opt.dataZoom.map((dz: any, i: number) => {
      if (!dz) return dz;
      if (dz.type == null) dz.type = i === 1 ? 'inside' : 'slider';
      return dz;
    });
  }

  if (opt.tooltip && typeof opt.tooltip.formatter === 'string') {
    const s: string = opt.tooltip.formatter.trim();
    if (s.startsWith('function')) {
      // eslint-disable-next-line no-new-func
      opt.tooltip.formatter = new Function(`return (${s})`)();
    }
  }

  if (opt.grid && typeof opt.grid === 'object') {
    opt.grid.containLabel = true;
  }

  // --- DPR-aware global title/subtitle (already done before, keep it) ---
  const baseTitle = opt.title?.textStyle?.fontSize ?? 18;
  const baseSub = opt.title?.subtextStyle?.fontSize ?? 12;
  const baseLegend = 12;
  opt.title = {
    ...(opt.title ?? {}),
    textStyle: { ...(opt.title?.textStyle ?? {}), fontSize: scaleFontByDpr(baseTitle, dpr) },
    subtextStyle: { ...(opt.title?.subtextStyle ?? {}), fontSize: scaleFontByDpr(baseSub, dpr) },
  };

  opt.legend = {
    ...(opt.legend ?? {}),
    textStyle: {
      fontSize: scaleFontByDpr(baseLegend, dpr),
    },
  };

  // --- DPR-aware GAUGE series ---
  if (Array.isArray(opt.series)) {
    opt.series = opt.series.map((s: any) => {
      if (!s || s.type !== 'gauge') return s;

      // 1) Title inside the series (requested)
      if (s.title) {
        const base = 18;
        s.title = { ...s.title, fontSize: scaleFontByDpr(base, dpr) };
      }

      // 2) Detail number (requested)
      if (s.detail) {
        const base = s.detail.fontSize ?? 28;
        s.detail = { ...s.detail, fontSize: scaleFontByDpr(base, dpr) };
        // If formatter is shipped as string
        if (typeof s.detail.formatter === 'string') {
          const t = s.detail.formatter.trim();
          if (t.startsWith('function')) {
            // eslint-disable-next-line no-new-func
            s.detail.formatter = new Function(`return (${t})`)();
          }
        }
      }

      // --- Optional but recommended for visual consistency ---
      // Axis labels
      if (s.axisLabel) {
        const base = s.axisLabel.fontSize ?? 12;
        s.axisLabel = { ...s.axisLabel, fontSize: scaleFontByDpr(base, dpr) };
      }

      // Strokes / widths
      if (s.axisLine?.lineStyle?.width != null) {
        s.axisLine.lineStyle.width = scaleLinearByDpr(s.axisLine.lineStyle.width, dpr);
      }
      if (s.axisTick) {
        if (s.axisTick.length != null) s.axisTick.length = scaleLinearByDpr(s.axisTick.length, dpr);
        if (s.axisTick.lineStyle?.width != null) {
          s.axisTick.lineStyle.width = scaleLinearByDpr(s.axisTick.lineStyle.width, dpr);
        }
      }
      if (s.splitLine) {
        if (s.splitLine.length != null) s.splitLine.length = scaleLinearByDpr(s.splitLine.length, dpr);
        if (s.splitLine.lineStyle?.width != null) {
          s.splitLine.lineStyle.width = scaleLinearByDpr(s.splitLine.lineStyle.width, dpr);
        }
      }
      if (s.pointer?.width != null) {
        s.pointer.width = scaleLinearByDpr(s.pointer.width, dpr);
      }

      return s;
    });
  }

  return opt;
}
